import ComplaintScopeFilters, { appendOriginParams } from '../../components/ComplaintScopeFilters';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { Dialog } from 'primereact/dialog';
import { appendWeeklySheets } from './IncidentWeeklyReport';
import './IncidentReport.css';
import { Tag } from 'primereact/tag';
import { useLocation, useNavigate } from 'react-router-dom';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import IncidentImpactEditor from './IncidentImpactEditor';
import { evaluationLabel, evaluationDetail } from './incidentEvaluation';
import { salesMoney, salesState, scenarioState } from './incidentSalesColumns';
import { categoryLabels, impactLabels, labelOptions } from './incidentImpact';

const panamaDateKey = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
};

const initialFilters = () => {
    const today = panamaDateKey();
    return {
        from: `${today.slice(0, 7)}-01`, to: today, levelOne: '', levelTwo: '', route: '',
        location: '', machineId: '', customer: '', currentArea: '', responsible: '', status: '', category: '', impact: '', evaluationBasis: '', origin: '', teamId: ''
    };
};

const statusLabels = {
    open: 'Abierto', in_progress: 'En progreso', awaiting_customer: 'Esperando cliente',
    pending: 'Pendiente', forwarded: 'Enviado', close: 'Cerrado', system_close: 'Cerrado automáticamente'
};

const statusOptions = [{ label: 'Todos', value: '' }, ...Object.entries(statusLabels).map(([value, label]) => ({ value, label }))];

const buildParams = (filters, page, rows, exportAll = false) => {
    const params = new URLSearchParams();
    appendOriginParams(params, filters.origin);
    Object.entries(filters).forEach(([key, value]) => value && key !== 'origin' && params.set(key, value));
    params.set('page', String(page + 1));
    params.set('limit', String(rows));
    if (exportAll) params.set('export', '1');
    return params.toString();
};

const IncidentReport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [draftFilters, setDraftFilters] = useState(initialFilters);
    const [filters, setFilters] = useState(initialFilters);
    const [periodError, setPeriodError] = useState('');
    const [periodMode, setPeriodMode] = useState('month');
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [exportError, setExportError] = useState('');
    const [weeklyReport, setWeeklyReport] = useState(null);
    const [weeklyLoading, setWeeklyLoading] = useState(true);
    const [weeklyError, setWeeklyError] = useState('');
    const [revision, setRevision] = useState(0);
    const weeklyRequestId = useRef(0);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(25);
    const [options, setOptions] = useState({});
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);
    const [salesReport, setSalesReport] = useState(null);
    const [salesLoading, setSalesLoading] = useState(true);
    const [salesError, setSalesError] = useState('');
    const salesRequestId = useRef(0);
    const salesByMachine = useMemo(() => new Map((salesReport?.rows || []).map((row) => [row.id, row])), [salesReport]);
    const scenarioByTicket = useMemo(() => new Map((salesReport?.scenarioTickets || []).map((row) => [row.id, row])), [salesReport]);
    const requestId = useRef(0);

    const load = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const response = await handleGetRequest(endPoints.getIncidentReport(buildParams(filters, page, rows)), false);
            if (id !== requestId.current) return;
            if (!response?.data) { setData([]); setTotal(0); setError('No se pudo cargar el reporte. Verifique el rango (máximo 366 días), sus permisos o vuelva a intentar.'); return; }
            setData(response?.data?.rows || []);
            setTotal(response?.data?.total || 0);
            setOptions(response?.data?.options || {});

        } catch {
            if (id === requestId.current) { setData([]); setTotal(0); setError('No se pudo cargar el reporte. Vuelva a aplicar los filtros.'); }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [filters, page, rows]);

    useEffect(() => { load(); return () => { requestId.current++; }; }, [load]);
    const loadSales = useCallback(async () => {
        const id = ++salesRequestId.current;
        setSalesLoading(true); setSalesError(''); setSalesReport(null);
        try {
            const response = await handleGetRequest(endPoints.incidentSalesImpact(new URLSearchParams({ from: filters.from, to: filters.to }).toString()), false);
            if (id !== salesRequestId.current) return;
            if (!response?.data || response.data.from !== filters.from || response.data.to !== filters.to) throw new Error('Missing range data');
            setSalesReport(response.data);
        } catch {
            if (id === salesRequestId.current) setSalesError('No se pudieron cargar las ventas del rango. Vuelva a aplicar los filtros.');
        } finally { if (id === salesRequestId.current) setSalesLoading(false); }
    }, [filters]);
    useEffect(() => { loadSales(); return () => { salesRequestId.current++; }; }, [loadSales]);
    useEffect(() => {
        const id = ++weeklyRequestId.current;
        setWeeklyLoading(true); setWeeklyError(''); setWeeklyReport(null);
        const loadWeekly = async () => {
            try {
                const params = buildParams(filters, 0, 200) + '&view=weekly';
                const response = await handleGetRequest(endPoints.getIncidentReport(params), false);
                if (id !== weeklyRequestId.current) return;
                if (!response?.data?.period || response.data.from !== filters.from || response.data.to !== filters.to) throw new Error('Missing weekly report');
                setWeeklyReport({ ...response.data, appliedFilters: { ...filters } });
            } catch {
                if (id === weeklyRequestId.current) setWeeklyError('No se pudo preparar el Excel. Revise el período o vuelva a seleccionar el mes.');
            } finally { if (id === weeklyRequestId.current) setWeeklyLoading(false); }
        };
        loadWeekly();
        return () => { weeklyRequestId.current++; };
    }, [filters, revision]);
    const salesReady = salesReport?.from === filters.from && salesReport?.to === filters.to && !salesLoading;
    const salesCell = (item, field) => {
        if (!salesReady) return salesLoading ? 'Cargando…' : 'No disponible';
        const row = salesByMachine.get(item.machineRecordId);
        if (field === 'status') return salesState(item, row);
        if (field === 'coverage') return row ? `${row.recordedDays} / ${row.expectedDays} días` : 'Sin determinar';
        return <span title="Subtotal por máquina repetido entre tickets: no sumar filas. Solo días cerrados con datos válidos; faltantes no son cero.">{field === 'estimatedLoss' && row?.[field] == null ? 'No estimable' : salesMoney(row?.[field])}</span>;
    };
    const scenarioCell = (item, field) => {
        if (!salesReady) return salesLoading ? 'Cargando…' : 'No disponible';
        const scenario = scenarioByTicket.get(item.id), machine = salesByMachine.get(item.machineRecordId);
        if (field === 'status') return scenarioState(scenario, machine);
        if (field === 'hours') return scenario?.assumedHours ?? 'Sin determinar';
        if (!scenario || scenario.assumedHours == null) return 'No estimable';
        if (!scenario.technical || scenario.assumedHours === 0) return salesMoney(0);
        return machine?.scenarioRisk == null ? 'No estimable' : salesMoney(machine.scenarioRisk);
    };

    const selectMonths = (firstMonth, lastMonth = firstMonth) => {
        const today = panamaDateKey();
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(firstMonth) || !/^\d{4}-(0[1-9]|1[0-2])$/.test(lastMonth)) return;
        if (lastMonth < firstMonth || lastMonth > today.slice(0, 7)) {
            setPeriodError('Seleccione un mes final igual o posterior al inicial, hasta el mes actual.'); return;
        }
        const [year, month] = lastMonth.split('-').map(Number);
        const lastDay = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
        const from = `${firstMonth}-01`, to = lastDay > today ? today : lastDay;
        if ((new Date(to) - new Date(from)) / 86400000 >= 366) {
            setPeriodError('Seleccione un rango máximo de 366 días.'); return;
        }
        const selected = { ...initialFilters(), from, to };
        setPeriodError(''); setDraftFilters(selected); setFilters(selected); setPage(0);
    };
    const setFilter = (key, value) => setDraftFilters((current) => ({ ...current, [key]: value }));
    const applyFilters = () => { setPeriodError(''); setPage(0); setFilters({ ...draftFilters }); setFiltersVisible(false); };
    const clearFilters = () => {
        const cleared = initialFilters();
        setPeriodError('');
        setPeriodMode('month'); setFiltersVisible(false);
        setDraftFilters(cleared);
        setPage(0);
        setFilters(cleared);
    };

    const formatDate = (value) => value ? new Intl.DateTimeFormat('es-PA', {
        timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date(value)) : '-';

    const exportExcel = async () => {
        if (!weeklyReport?.period || weeklyLoading) return;
        const version = weeklyRequestId.current;
        setExporting(true); setExportError('');
        try {
            const module = await import('xlsx');
            if (version !== weeklyRequestId.current) return;
            const XLSX = module.default || module;
            const workbook = XLSX.utils.book_new();
            appendWeeklySheets(XLSX, workbook, weeklyReport);
            XLSX.writeFile(workbook, `incidencias_${filters.from}_${filters.to}.xlsx`, { compression: true });
        } catch {
            if (version === weeklyRequestId.current) setExportError('No se pudo exportar el reporte. Vuelva a intentar.');
        } finally { setExporting(false); }
    };
    const monthDate = (value) => {
        const [year, month] = value.slice(0, 7).split('-').map(Number);
        return new Date(year, month - 1, 1, 12);
    };
    const monthKey = (value) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
    const activeFilterCount = Object.entries(filters).filter(([key, value]) => !['from', 'to'].includes(key) && value).length;
    const rangeLabel = `${formatDate(`${filters.from}T12:00:00-05:00`)} – ${formatDate(`${filters.to}T12:00:00-05:00`)}`;
    const openChat = (item) => {
        navigate('/complaints/chat', {
            state: {
                complaint: { _id: item.id, status: item.status },
                returnTo: `${location.pathname}${location.search}`
            }
        });
    };

    return (
        <div className="incident-report card">
            <div className="incident-report-heading">
                <div><h2>Reporte de incidencias</h2><p>Consulta el detalle por mes y descarga los resúmenes en un solo Excel.</p></div>
                <Button label="Exportar Excel" icon="pi pi-file-excel" className="incident-export" loading={exporting}
                    disabled={exporting || weeklyLoading || !weeklyReport?.period} onClick={exportExcel} />
            </div>
            <p className="text-600">Tickets creados en el período seleccionado, sin provisionales ni abandonados. La tabla muestra el estado actual; el Excel distingue estado actual y estado al cierre del período.</p>
            <div className="incident-filters-bar">
                <div className="incident-period-mode"><span id="incident-period-label" className="incident-field-label">Período</span>
                    <SelectButton aria-labelledby="incident-period-label" value={periodMode} options={[{ label: 'Un mes', value: 'month' }, { label: 'Varios meses', value: 'range' }]}
                        onChange={(event) => { if (!event.value) return; setPeriodMode(event.value); if (event.value === 'month') selectMonths(draftFilters.from.slice(0, 7)); }} />
                </div>
                <div className="incident-month-field"><label htmlFor="incident-month" className="incident-field-label">{periodMode === 'range' ? 'Mes inicial' : 'Mes'}</label>
                    <Calendar inputId="incident-month" value={monthDate(draftFilters.from)} view="month" dateFormat="MM yy" locale="es" showIcon readOnlyInput
                        maxDate={monthDate(panamaDateKey())} onChange={(event) => { if (event.value) selectMonths(monthKey(event.value), periodMode === 'range' && draftFilters.to.slice(0, 7) >= monthKey(event.value) ? draftFilters.to.slice(0, 7) : monthKey(event.value)); }} />
                </div>
                {periodMode === 'range' && <div className="incident-month-field"><label htmlFor="incident-end-month" className="incident-field-label">Mes final</label>
                    <Calendar inputId="incident-end-month" value={monthDate(draftFilters.to)} view="month" dateFormat="MM yy" locale="es" showIcon readOnlyInput
                        minDate={monthDate(draftFilters.from)} maxDate={monthDate(panamaDateKey())} onChange={(event) => { if (event.value) selectMonths(draftFilters.from.slice(0, 7), monthKey(event.value)); }} />
                </div>}
                <Button label={activeFilterCount ? `Más filtros (${activeFilterCount})` : 'Más filtros'} icon="pi pi-sliders-h" className="p-button-outlined incident-more-filters"
                    onClick={() => { setDraftFilters({ ...filters }); setFiltersVisible(true); }} />
            </div>
            <div className="incident-report-meta"><span><i className="pi pi-calendar" aria-hidden="true" /> {rangeLabel}</span><span>{total.toLocaleString('es-PA')} incidencias · Panamá</span></div>
            <Dialog header="Filtros adicionales" visible={filtersVisible} onHide={() => setFiltersVisible(false)} modal draggable={false}
                className="incident-filter-dialog" style={{ width: '42rem' }} breakpoints={{ '768px': '94vw' }}
                footer={<div className="incident-dialog-actions"><Button label="Limpiar" className="p-button-text" onClick={clearFilters} /><Button label="Aplicar filtros" icon="pi pi-check" onClick={applyFilters} /></div>}>
                <p className="text-600">Opcionales. Para consultar el mes completo, deja todos los campos vacíos.</p>
                <ComplaintScopeFilters id="incident" origin={draftFilters.origin} onOriginChange={value => setFilter('origin', value)} teamId={draftFilters.teamId} onTeamChange={value => setFilter('teamId', value)} />
                <div className="grid p-fluid">
                    <div className="col-12 md:col-6"><label htmlFor="incident-route" className="incident-field-label">Ruta</label><Dropdown inputId="incident-route" value={draftFilters.route}
                        options={[...new Set([...(options.route || []), ...(weeklyReport?.options?.route || [])])].map((value) => ({ label: value, value }))} placeholder="Todas las rutas" showClear filter onChange={(event) => setFilter('route', event.value || '')} /></div>
                    <div className="col-12 md:col-6"><label htmlFor="incident-customer" className="incident-field-label">Cliente</label><InputText id="incident-customer" value={draftFilters.customer} placeholder="Nombre del cliente" onChange={(event) => setFilter('customer', event.target.value)} /></div>
                    <div className="col-12 md:col-6"><label htmlFor="incident-status" className="incident-field-label">Estado actual</label><Dropdown inputId="incident-status" value={draftFilters.status} options={statusOptions} onChange={(event) => setFilter('status', event.value || '')} /></div>
                    <div className="col-12 md:col-6"><label htmlFor="incident-category" className="incident-field-label">Tipo de incidencia</label><Dropdown inputId="incident-category" value={draftFilters.category} options={labelOptions(categoryLabels)} placeholder="Todos los tipos" showClear onChange={(event) => setFilter('category', event.value || '')} /></div>
                </div>
            </Dialog>
            {periodError && <p role="alert" className="text-red-600">{periodError}</p>}
            {error && <p role="alert" className="text-red-600">{error}</p>}
            {salesError && <p role="alert" className="text-red-600">{salesError}</p>}
            {weeklyError && <p role="alert" className="text-red-600">{weeklyError}</p>}
            {exportError && <p role="alert" className="text-red-600">{exportError}</p>}
                    <DataTable value={data} lazy paginator first={page * rows} rows={rows} totalRecords={total} loading={loading}
                        onPage={(event) => { setPage(event.page); setRows(event.rows); }} rowsPerPageOptions={[15, 25, 50, 100]}
                        emptyMessage="No se encontraron incidencias" responsiveLayout="scroll" stripedRows>
                        <Column field="customerName" header="Nombre del cliente" />
                        <Column field="category" header="Tipo de incidencia" body={(item) => categoryLabels[item.category]} />
                        <Column field="levelOne" header="Clasificación original" />
                        <Column field="levelTwo" header="Subtipo" />
                        <Column field="detail" header="Detalle del incidente" style={{ minWidth: '20rem' }} />
                        <Column field="route" header="Ruta" />
                        <Column field="location" header="Ubicación" />
                        <Column field="machineId" header="ID Máquina" />
                        <Column field="incidentDate" header="Fecha de incidencia" body={(item) => formatDate(item.incidentDate)} />
                        <Column field="closedAt" header="Fecha de cierre" body={(item) => formatDate(item.closedAt)} />
                        <Column field="status" header="Estado actual" body={(item) => <Tag value={statusLabels[item.status] || item.status} severity={item.status === 'close' || item.status === 'system_close' ? 'success' : 'info'} />} />
                        <Column field="currentArea" header="Área actual" />
                        <Column field="responsible" header="Responsable" />
                        <Column field="openDays" header="Días de ticket (no parada)" />
                        <Column header="Impacto actual" body={(item) => impactLabels[item.impact]} />
                        <Column header="Base de evaluación" body={(item) => <div><Tag value={evaluationLabel(item)} severity={item.evaluationBasis === 'confirmed' ? 'success' : item.evaluationBasis === 'suggested' ? 'warning' : 'info'} /><small className="block mt-2">{evaluationDetail(item)}</small></div>} style={{ minWidth: '19rem' }} />
                        <Column header="Horas detenida registradas" body={(item) => item.stoppedHours ?? 'Sin determinar'} />
                        <Column header="Horas afectación parcial" body={(item) => item.partialHours ?? 'Sin determinar'} />
                        <Column header="Período en curso" body={(item) => item.openPeriod ? 'Sí' : 'No'} />
                        <Column header="Horas de parada supuestas" body={(item) => scenarioCell(item, 'hours')} />
                        <Column header="Ventas del rango USD (por máquina)" body={(item) => salesCell(item, 'recordedSales')} />
                        <Column header="Cobertura de ventas" body={(item) => salesCell(item, 'coverage')} />
                        <Column header="Pérdida por paradas confirmadas USD (por máquina)" body={(item) => salesCell(item, 'estimatedLoss')} />
                        <Column header="Estado del cálculo confirmado" body={(item) => salesCell(item, 'status')} style={{ minWidth: '15rem' }} />
                        <Column header={<span title="Subtotal estimable por máquina, repetido entre sus tickets técnicos: no sumar filas. Días completos cerrados; faltantes no son cero.">Ventas en riesgo USD (supuesto por máquina)</span>} body={(item) => scenarioCell(item, 'risk')} />
                        <Column header="Estado del escenario" body={(item) => scenarioCell(item, 'status')} style={{ minWidth: '19rem' }} />
                        <Column header="Registro" body={(item) => <Button label="Registrar impacto" outlined onClick={() => setEditing(item)} />} />
                        <Column header="Chat" body={(item) => <Button label="Abrir chat" icon="pi pi-comments" className="p-button-rounded p-button-sm" style={{ backgroundColor: '#2563EB', border: 'none', whiteSpace: 'nowrap' }} onClick={() => openChat(item)} />} />
                    </DataTable>
                    {editing && <IncidentImpactEditor ticket={editing} onHide={() => setEditing(null)} onSaved={() => { setEditing(null); load(); loadSales(); setRevision((value) => value + 1); }} />}
        </div>
    );
};

export default IncidentReport;
